<template>
  <section>
    <h2>比較編碼</h2>
    <fieldset :disabled="isComparing || isDeletingResult">
      <b-form-group label="選擇準則庫" label-for="qa-compare-criterion">
        <b-form-select
          id="qa-compare-criterion"
          v-model="selectedCriterionId"
          :disabled="isLoadingResults"
          @change="loadResults"
        >
          <b-form-select-option :value="null" disabled>
            請選擇準則庫
          </b-form-select-option>

          <b-form-select-option
            v-for="entry in criteria"
            :key="entry.id"
            :value="entry.id"
          >
            {{ entry.name }}
          </b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-button
        variant="outline-secondary"
        class="mb-3"
        :disabled="!selectedCriterionId || isLoadingResults"
        @click="loadResults(selectedCriterionId)"
      >
        <b-icon icon="arrow-clockwise" aria-hidden="true" />
        更新結果列表
      </b-button>

      <p v-if="isLoadingResults">載入中…</p>

      <b-alert :show="Boolean(resultsError)" variant="danger">
        {{ resultsError }}
      </b-alert>
      <p
        v-if="
          codingType &&
          !isLoadingResults &&
          !resultsError &&
          aiResults.length === 0
        "
      >
        此準則尚無 AI 結果。
      </p>

      <p
        v-if="
          codingType &&
          !isLoadingResults &&
          !resultsError &&
          humanResults.length === 0
        "
      >
        此準則尚無人工結果。
      </p>
      <b-form-group label="AI 紀錄">
        <div
          v-for="record in aiResults"
          :key="record.id"
          class="d-flex align-items-center justify-content-between mb-2"
        >
          <b-form-checkbox
            v-model="aiResultIds"
            :value="record.id"
            :disabled="isLoadingResults"
          >
            {{ record.name }}（ID：{{ record.id }}）
          </b-form-checkbox>
          <b-button
            size="sm"
            variant="outline-danger"
            class="ml-2"
            :disabled="isLoadingResults || isDeletingResult"
            title="刪除結果"
            aria-label="刪除結果"
            @click="deleteResult(record)"
          >
            <b-icon icon="trash" aria-hidden="true" />
          </b-button>
        </div>
      </b-form-group>

      <b-form-group label="人類紀錄">
        <div
          v-for="record in humanResults"
          :key="record.id"
          class="d-flex align-items-center justify-content-between mb-2"
        >
          <b-form-checkbox
            v-model="humanResultIds"
            :value="record.id"
            :disabled="isLoadingResults"
          >
            {{ record.name }}（ID：{{ record.id }}）
          </b-form-checkbox>
          <b-button
            size="sm"
            variant="outline-danger"
            class="ml-2"
            :disabled="isLoadingResults || isDeletingResult"
            title="刪除結果"
            aria-label="刪除結果"
            @click="deleteResult(record)"
          >
            <b-icon icon="trash" aria-hidden="true" />
          </b-button>
        </div>
      </b-form-group>
      <b-form-group v-if="calibrations.length" label="校準紀錄">
        <div
          v-for="record in calibrations"
          :key="record.id"
          class="d-flex align-items-center justify-content-between mb-2"
        >
          <span>
            {{ calibrationStatusText(record.status) }}（ID：{{ record.id }}，
            {{ formatDateTime(record.createdAt) }}）
          </span>
          <b-button
            size="sm"
            variant="outline-danger"
            class="ml-2"
            :disabled="
              isLoadingResults ||
              isDeletingResult ||
              record.status === 'running'
            "
            :title="
              record.status === 'running' ? '進行中的校準請先取消' : '刪除校準'
            "
            aria-label="刪除校準"
            @click="deleteCalibration(record)"
          >
            <b-icon icon="trash" aria-hidden="true" />
          </b-button>
        </div>
      </b-form-group>
      <p>
        已選 AI 紀錄 {{ aiResultIds.length }} 筆， 人類紀錄
        {{ humanResultIds.length }} 筆。
      </p>

      <b-form-group label="統計方法" label-for="qa-compare-method">
        <b-form-select
          id="qa-compare-method"
          v-model="method"
          :options="methodOptions"
        />
      </b-form-group>

      <b-form-group
        v-if="method === 'kappa'"
        label="最低 Kappa"
        label-for="qa-min-kappa"
      >
        <b-form-input
          id="qa-min-kappa"
          v-model.number="minKappa"
          type="number"
          min="-1"
          max="1"
          step="0.01"
        />
      </b-form-group>

      <div v-if="method === 'pearson'">
        <b-form-group label="最低相關係數 r" label-for="qa-min-r">
          <b-form-input
            id="qa-min-r"
            v-model.number="minR"
            type="number"
            min="-1"
            max="1"
            step="0.01"
          />
        </b-form-group>

        <b-form-group label="最大 p 值" label-for="qa-max-p">
          <b-form-input
            id="qa-max-p"
            v-model.number="maxP"
            type="number"
            min="0"
            max="1"
            step="0.01"
          />
        </b-form-group>
      </div>
    </fieldset>
    <b-alert :show="Boolean(deleteResultError)" variant="danger">
      {{ deleteResultError }}
    </b-alert>
    <b-alert :show="Boolean(compareError)" variant="warning">
      {{ compareError }}
    </b-alert>

    <b-button
      variant="primary"
      :disabled="Boolean(compareError) || isComparing || isDeletingResult"
      @click="runCompare"
    >
      <b-icon icon="bar-chart" aria-hidden="true" />
      {{ isComparing ? "比較中…" : "執行比較" }}
    </b-button>

    <b-alert :show="Boolean(compareRequestError)" variant="danger" class="mt-3">
      {{ compareRequestError }}
    </b-alert>

    <div v-if="compareResult" class="mt-3">
      <h3>比較結果</h3>
      <b-button
        variant="outline-primary"
        :disabled="
          !compareResult.human.passed || isComparing || isDeletingResult
        "
        @click="selectCalibration"
      >
        <b-icon icon="gear" aria-hidden="true" />
        設定 AI 自動校準
      </b-button>
      <p v-if="!compareResult.human.passed">
        人類編碼員間未達共識，不允許 AI 校正。
      </p>
      <p>
        人類結果：
        {{ compareResult.human.passed ? "通過" : "未通過" }}
      </p>
      <p v-if="compareResult.human.stat">
        Kappa：
        {{
          compareResult.human.stat.kappa === null
            ? "無法計算"
            : compareResult.human.stat.kappa
        }}
      </p>
      <p v-if="humanResultIds.length === 1">
        僅一筆人類紀錄，直接作為 gold，未計算人類間一致性。
      </p>
      <div v-if="compareResult.human.pairs">
        <h4>人類間 Pearson 比較</h4>
        <b-table-simple responsive small>
          <b-thead>
            <b-tr>
              <b-th>紀錄配對</b-th><b-th>r</b-th> <b-th>p</b-th
              ><b-th>樣本數</b-th><b-th>結果</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="pair in compareResult.human.pairs"
              :key="pair.resultIds.join('-')"
            >
              <b-td>{{ pair.resultIds.map(resultName).join(" ／ ") }}</b-td>
              <b-td>{{ formatStat(pair.stat.r) }}</b-td>
              <b-td>{{ formatStat(pair.stat.p) }}</b-td>
              <b-td>{{ pair.stat.n }}</b-td>
              <b-td>{{ pair.passed ? "通過" : "未通過" }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <template v-if="humanResultIds.length >= 2">
        <h4>人類編碼員不一致題目</h4>
        <p v-if="humanMismatchRows.length === 0">所有題目的人類編碼皆一致。</p>
        <b-table-simple v-else responsive small>
          <b-thead>
            <b-tr>
              <b-th>題號</b-th>
              <b-th>題目</b-th>
              <b-th v-for="record in selectedHumanResults" :key="record.id">
                {{ record.name }}
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="row in humanMismatchRows" :key="row.no">
              <b-td>{{ row.no }}</b-td>
              <b-td>{{ row.text }}</b-td>
              <b-td v-for="record in selectedHumanResults" :key="record.id">
                {{ formatCode(record.codes && record.codes[String(row.no)]) }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </template>

      <h4>Gold 編碼</h4>
      <b-table-simple responsive small>
        <b-thead>
          <b-tr><b-th>題號</b-th><b-th>Gold</b-th></b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="(code, no) in compareResult.gold" :key="no">
            <b-td>{{ no }}</b-td
            ><b-td>{{ code }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <p v-if="compareResult.tieNos && compareResult.tieNos.length">
        平手題號：{{
          compareResult.tieNos.join("、")
        }}；採用第一位選取編碼員的結果。
      </p>

      <h4>AI 與 Gold 比較</h4>
      <p v-if="compareResult.ai.length === 0">本次未選取 AI 紀錄。</p>
      <div v-for="entry in compareResult.ai" :key="entry.resultId" class="mb-3">
        <strong>{{ resultName(entry.resultId) }}</strong>
        <p>結果：{{ entry.passed ? "通過" : "未通過" }}</p>
        <p v-if="'kappa' in entry.stat">
          Kappa：{{ formatStat(entry.stat.kappa) }}
        </p>
        <p v-else>
          r：{{ formatStat(entry.stat.r) }}； p：{{
            formatStat(entry.stat.p)
          }}； 樣本數：{{ entry.stat.n }}
        </p>
        <p>
          編碼值不同的題號：
          {{ entry.mismatchNos.length ? entry.mismatchNos.join("、") : "無" }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";
export default {
  name: "QACompare",

  data() {
    return {
      codingType: "",
      criteria: [],
      selectedCriterionId: null,
      isLoadingResults: false,
      resultsError: "",
      isDeletingResult: false,
      deleteResultError: "",
      isComparing: false,
      compareRequestError: "",
      compareResult: null,
      method: "kappa",
      minKappa: 0.7,
      minR: 0.7,
      maxP: 0.05,
      aiResults: [],
      humanResults: [],
      aiResultIds: [],
      humanResultIds: [],
      calibrations: [],
      criterionItems: [],
    };
  },

  methods: {
    async loadCriteria() {
      if (this.isLoadingResults || this.isComparing) return;

      this.isLoadingResults = true;
      this.resultsError = "";
      this.deleteResultError = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/criteria`,
        );

        this.criteria = response.data;
      } catch (error) {
        this.resultsError =
          error.response?.data?.message || error.message || "讀取準則列表失敗";
      } finally {
        this.isLoadingResults = false;
      }
    },
    async loadResults(id) {
      if (!id || this.isLoadingResults || this.isComparing) return;

      this.isLoadingResults = true;
      this.resultsError = "";
      this.aiResults = [];
      this.humanResults = [];
      this.aiResultIds = [];
      this.humanResultIds = [];
      this.calibrations = [];
      this.criterionItems = [];
      this.codingType = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const url = base.replace(/\/+$/, "");

        const criterionResponse = await axios.get(
          `${url}/api/qa/criteria/${id}`,
        );

        const [resultsResponse, calibrationsResponse] = await Promise.all([
          axios.get(`${url}/api/qa/results`, {
            params: { criterionId: id, includeCodes: "1" },
          }),
          axios.get(`${url}/api/qa/calibrations`, {
            params: { criterionId: id },
          }),
        ]);

        this.codingType = criterionResponse.data.codingType;
        this.criterionItems = criterionResponse.data.items || [];
        this.aiResults = resultsResponse.data.filter(
          (record) => record.isAI === true,
        );
        this.humanResults = resultsResponse.data.filter(
          (record) => record.isAI === false,
        );
        this.calibrations = calibrationsResponse.data;
      } catch (error) {
        this.resultsError =
          error.response?.data?.message || error.message || "讀取結果列表失敗";
      } finally {
        this.isLoadingResults = false;
      }
    },
    async deleteResult(record) {
      if (this.isDeletingResult || this.isComparing || this.isLoadingResults)
        return;

      this.isDeletingResult = true;
      this.deleteResultError = "";

      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `確定刪除「${record.name}」（ID：${record.id}）？此操作無法復原。`,
          {
            title: "刪除結果",
            okTitle: "刪除",
            cancelTitle: "取消",
            okVariant: "danger",
            centered: true,
          },
        );
        if (!confirmed) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        await axios.delete(`${base.replace(/\/+$/, "")}/api/qa/results`, {
          params: { id: record.id },
        });

        this.compareResult = null;
        this.compareRequestError = "";
        await this.loadResults(this.selectedCriterionId);
      } catch (error) {
        this.deleteResultError =
          error.response?.data?.message || error.message || "刪除結果失敗";
      } finally {
        this.isDeletingResult = false;
      }
    },
    async deleteCalibration(record) {
      if (this.isDeletingResult || this.isComparing || this.isLoadingResults)
        return;

      this.isDeletingResult = true;
      this.deleteResultError = "";

      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `確定刪除校準紀錄（ID：${record.id}）？刪除後，它引用的人類紀錄就能刪除。此操作無法復原。`,
          {
            title: "刪除校準",
            okTitle: "刪除",
            cancelTitle: "取消",
            okVariant: "danger",
            centered: true,
          },
        );
        if (!confirmed) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        await axios.delete(
          `${base.replace(/\/+$/, "")}/api/qa/calibrations/${encodeURIComponent(
            record.id,
          )}`,
        );

        // 下方校準區塊可能正顯示這筆紀錄，一併關閉
        this.$emit("calibration-selected", null);
        await this.loadResults(this.selectedCriterionId);
      } catch (error) {
        this.deleteResultError =
          error.response?.data?.message || error.message || "刪除校準失敗";
      } finally {
        this.isDeletingResult = false;
      }
    },
    calibrationStatusText(status) {
      const names = {
        running: "進行中",
        passed: "驗證通過",
        not_passed: "未達標",
        failed: "發生錯誤",
        cancelled: "已取消",
      };

      return names[status] || "未知狀態";
    },
    formatCode(code) {
      return code === undefined || code === null || code === ""
        ? "未編碼"
        : String(code);
    },
    formatDateTime(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
    },
    async runCompare() {
      if (this.compareError || this.isComparing || this.isDeletingResult)
        return;

      this.isComparing = true;
      this.compareRequestError = "";
      this.compareResult = null;

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const response = await axios.post(
          `${base.replace(/\/+$/, "")}/api/qa/compare`,
          {
            criterionId: this.selectedCriterionId,
            humanResultIds: [...this.humanResultIds],
            aiResultIds: [...this.aiResultIds],
            method: this.method,
            thresholds: {
              minKappa: this.minKappa,
              minR: this.minR,
              maxP: this.maxP,
            },
          },
        );

        this.compareResult = response.data;
      } catch (error) {
        this.compareRequestError =
          error.response?.data?.message || error.message || "比較失敗";
      } finally {
        this.isComparing = false;
      }
    },

    formatStat(value) {
      return Number.isFinite(value) ? value.toFixed(4) : "無法計算";
    },
    resultName(id) {
      const record = [...this.humanResults, ...this.aiResults].find(
        (entry) => entry.id === id,
      );
      return record ? `${record.name}（ID：${id}）` : `ID：${id}`;
    },
    selectCalibration() {
      if (
        !this.compareResult?.human?.passed ||
        this.compareError ||
        this.isComparing ||
        this.isLoadingResults ||
        this.isDeletingResult
      )
        return;

      this.$emit("calibration-selected", {
        criterionId: this.selectedCriterionId,
        humanResultIds: [...this.humanResultIds],
        method: this.method,
        thresholds: {
          minKappa: this.minKappa,
          minR: this.minR,
          maxP: this.maxP,
        },
      });
    },
  },

  computed: {
    methodOptions() {
      const options = [];

      if (!["label", "scale", "score"].includes(this.codingType)) {
        return options;
      }

      if (this.codingType !== "score") {
        options.push({ value: "kappa", text: "Kappa" });
      }

      if (this.codingType !== "label") {
        options.push({ value: "pearson", text: "Pearson" });
      }

      return options;
    },

    thresholdError() {
      if (this.method === "kappa") {
        if (
          !Number.isFinite(this.minKappa) ||
          this.minKappa < -1 ||
          this.minKappa > 1
        ) {
          return "最低 Kappa 必須是 -1 到 1 之間的數字";
        }

        return "";
      }

      if (this.method === "pearson") {
        if (!Number.isFinite(this.minR) || this.minR < -1 || this.minR > 1) {
          return "最低相關係數 r 必須是 -1 到 1 之間的數字";
        }

        if (!Number.isFinite(this.maxP) || this.maxP <= 0 || this.maxP > 1) {
          return "最大 p 值必須大於 0，且不超過 1";
        }

        return "";
      }

      return "請選擇有效的統計方法";
    },

    compareError() {
      if (!this.codingType || this.isLoadingResults || this.resultsError) {
        return "請先載入有效的準則與結果列表";
      }

      if (!this.humanResultIds.length) {
        return "請至少選取一筆人類紀錄";
      }

      const allowed = this.methodOptions.some(
        (option) => option.value === this.method,
      );

      if (!allowed) {
        return "目前編碼類型不支援這個統計方法";
      }

      return this.thresholdError;
    },

    // 依勾選順序排列，與比較時送出的 humanResultIds 一致
    selectedHumanResults() {
      return this.humanResultIds
        .map((id) => this.humanResults.find((record) => record.id === id))
        .filter(Boolean);
    },

    humanMismatchRows() {
      const records = this.selectedHumanResults;
      if (records.length < 2) return [];

      return this.criterionItems.filter((item) => {
        const key = String(item.no);
        const codes = records.map((record) => record.codes?.[key]);
        return codes.some((code) => code !== codes[0]);
      });
    },

    compareSettingsKey() {
      return JSON.stringify([
        this.selectedCriterionId,
        this.codingType,
        this.humanResultIds,
        this.aiResultIds,
        this.method,
        this.minKappa,
        this.minR,
        this.maxP,
      ]);
    },
  },

  watch: {
    codingType: {
      immediate: true,
      handler() {
        const allowed = this.methodOptions.map((option) => option.value);

        if (!allowed.includes(this.method)) {
          this.method = allowed[0];
        }
      },
    },
    compareSettingsKey() {
      this.compareResult = null;
      this.compareRequestError = "";
      this.$emit("calibration-selected", null);
    },
  },

  mounted() {
    this.loadCriteria();
  },
};
</script>
